import videojs from 'video.js';

const ErrorNames = {
    timeAnomaly: 'Time Anomaly'
}

const _lastPositionChange = new WeakMap();
const _firstPositionChangeOfSegment = new WeakMap();
const _tolerance = new WeakMap();
const _playbackRate = new WeakMap();

export default class ReportingPlayDetector extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        super(player, options);

        this.lastPositionChange = null;
        this.firstPositionChangeOfSegment = null;

        this.tolerance = 0.5; // The amount above/below the expected position that is still considered a hit.
        this.playbackRate = 1.0;
    }

    set firstPositionChangeOfSegment(change) {
        _firstPositionChangeOfSegment.set(this, change);
    }

    get firstPositionChangeOfSegment() {
        return _firstPositionChangeOfSegment.get(this);
    }

    set lastPositionChange(change) {
        _lastPositionChange.set(this, change);
    }

    get lastPositionChange() {
        return _lastPositionChange.get(this);
    }

    set tolerance(tolerance) {
        _tolerance.set(this, tolerance);
    }

    get tolerance() {
        return _tolerance.get(this);
    }

    set playbackRate(rate) {
        _playbackRate.set(this, rate);
    }

    get playbackRate() {
        return _playbackRate.get(this);
    }

    // True eventing is overkill. We just need a single function to call when each "event" occurs.
    segmentStartAction(startPosition) {};
    segmentEndAction(startPosition, endPosition, isImportant) {};

    handlePlaybackRateChange(rate) {
        this.playbackRate = rate;

        // TODO: Attempt minimize segment end actions caused by changes in playback rate.
    };

    handlePositionChange(position) {
        let playing;
        const clockTimeInSeconds = this.getCurrentTime() / 1000;
        const positionChange = { time: clockTimeInSeconds, position: position };

        if (this.lastPositionChange) {
            try {
                playing = this.isProbablyPlaying(this.lastPositionChange.time, this.lastPositionChange.position, positionChange.time, positionChange.position, this.playbackRate, this.tolerance);
            } catch (err) {
                if (err.name = ErrorNames.timeAnomaly) {
                    // Times should always be increasing.
                    // However, there are some conditions that can cause JavaScript to report a 
                    // time value that is earlier than the previous one. For example, the client 
                    // computer's time may have been updated via NTP.

                    this.log('Times provided by timeProvider were out of order.');
                }

                // We certainly can't say that we're playing. Bite the bullet and  end the
                // current segment so we can start fresh with a new segment.
                playing = false;
            }

            if (playing) {

                if (!this.firstPositionChangeOfSegment) {
                    this.startSegment();
                }

            } else { // Seek or something. Does not look to be "just playing along".
                this.endSegment();
            }
        }

        this.lastPositionChange = positionChange;
    };

    getCurrentTime() {
        const useModernMode = (window.performance && typeof window.performance.now === 'function');
        if (useModernMode) {
            return window.performance.now();
        } else {
            return new Date().getTime();
        }
    };

    endSegment(isImportantNotification) {
        isImportantNotification = isImportantNotification || false;

        if (this.firstPositionChangeOfSegment && this.lastPositionChange) {
            // Notify segment was played!
            this.segmentEndAction(this.firstPositionChangeOfSegment.position, this.lastPositionChange.position, isImportantNotification);
        }

        // Clear segment start.
        // This will result in the start of a new segment with the next qualifying position changes.
        this.firstPositionChangeOfSegment = null;
    };


    startSegment() {
        // Set segment start if empty.
        this.firstPositionChangeOfSegment = this.lastPositionChange;

        // Report segment start!
        this.segmentStartAction(this.firstPositionChangeOfSegment.position);
    }

    isProbablyPlaying(time1, position1, time2, position2, playbackRate, toleranceFactor) {
        let deltaTime, deltaPosition, expectedPositionChange;
        const enforceNonNegativeNumber = function (val) {
            if (typeof val !== 'number') {
                val = 0;
            }

            return Math.max(0, val);
        };

        time1 = enforceNonNegativeNumber(time1);
        position1 = enforceNonNegativeNumber(position1);
        time2 = enforceNonNegativeNumber(time2);
        position2 = enforceNonNegativeNumber(position2);
        playbackRate = enforceNonNegativeNumber(playbackRate);
        toleranceFactor = enforceNonNegativeNumber(toleranceFactor);

        if (time1 > time2) {
            // Non-increasing clock times represent a programming error so we throw!
            throw { name: 'Argument Error', message: 'time2 must be greater than time1.' };
        }

        if (position1 > position2) {
            // Non-increasing positions indicate a seek or perhaps a pause.
            return false;
        }

        deltaTime = time2 - time1;
        deltaPosition = position2 - position1;

        expectedPositionChange = deltaTime * playbackRate;

        return Math.abs(expectedPositionChange - deltaPosition) <= (expectedPositionChange * toleranceFactor);
    };
}
